import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CarFront, AlertCircle, Loader2, Download, Eye, Printer } from "lucide-react";

const INITIAL = {
  owner_name: "",
  motor_number: "",
  marca: "",
  linea: "",
  modelo: "",
  color: "",
  serie: "",
};

export default function PermitForm({ disabled, remaining, isAdmin, onCreated }) {
  const { authHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [lastPermit, setLastPermit] = useState(null);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const openPrintView = (folioShort) => {
    navigate(`/permits/${folioShort}/print`);
  };

  const handleDownloadPdf = (folioShort) => {
    const url = `${API}/permits/${folioShort}/pdf?token=${encodeURIComponent(token)}`;
    let iframe = document.getElementById("__pdf_dl_frame");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "__pdf_dl_frame";
      iframe.style.display = "none";
      document.body.appendChild(iframe);
    }
    iframe.src = url;
    toast.success(`Descargando PDF ${folioShort}...`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) {
      toast.error("No tienes permisos disponibles. Solicita al administrador.");
      return;
    }
    for (const k of Object.keys(INITIAL)) {
      if (!String(form[k]).trim()) {
        toast.error("Completa todos los campos");
        return;
      }
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/permits`, form, { headers: authHeaders });
      setLastPermit(res.data);
      setForm(INITIAL);
      onCreated && onCreated(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear el permiso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: lastPermit ? "2fr 1fr" : "1fr", gap: 16 }}>
      <Card>
        <CardHeader>
          <CardTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CarFront size={20} color="#f58238" />
            Nuevo permiso de circulación
          </CardTitle>
          <CardDescription>
            Permiso temporal para circular sin placa por trámite de emplacamiento. Vigencia: 30 días naturales.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {disabled && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", padding: "0.75rem 1rem", borderRadius: 8, marginBottom: 16, fontSize: "0.9rem" }}>
              <AlertCircle size={18} />
              Has alcanzado tu límite de permisos. Contacta al administrador para asignarte más.
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <h3 style={{ fontSize: "0.85rem", fontWeight: 600, textTransform: "uppercase", color: "#6b7280", letterSpacing: 0.5, margin: "0 0 12px 0" }}>Datos del propietario</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 20 }}>
              <Field label="Nombre del contribuyente" value={form.owner_name} onChange={(v) => onChange("owner_name", v.toUpperCase())} placeholder="Ej. PABLO SANCHEZ GARCIA" />
            </div>

            <h3 style={{ fontSize: "0.85rem", fontWeight: 600, textTransform: "uppercase", color: "#6b7280", letterSpacing: 0.5, margin: "0 0 12px 0" }}>Datos del vehículo</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
              <Field label="Marca" value={form.marca} onChange={(v) => onChange("marca", v.toUpperCase())} placeholder="VOLKSWAGEN" />
              <Field label="Línea / Submarca" value={form.linea} onChange={(v) => onChange("linea", v.toUpperCase())} placeholder="JETTA" />
              <Field label="Modelo (año)" value={form.modelo} onChange={(v) => onChange("modelo", v)} placeholder="2012" />
              <Field label="Color" value={form.color} onChange={(v) => onChange("color", v.toUpperCase())} placeholder="BLANCO" />
              <Field label="Número de motor" value={form.motor_number} onChange={(v) => onChange("motor_number", v.toUpperCase())} placeholder="CBP289041" />
              <Field label="Número de serie (NIV/VIN)" value={form.serie} onChange={(v) => onChange("serie", v.toUpperCase())} placeholder="3VWGV49M5CM022183" />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Button type="button" variant="outline" onClick={() => setForm(INITIAL)} disabled={loading}>
                Limpiar
              </Button>
              <Button type="submit" disabled={loading || disabled} style={{ backgroundColor: "#f58238", color: "white" }}>
                {loading ? <><Loader2 size={14} className="spin" style={{ marginRight: 6 }} /> Generando...</> : "Generar permiso"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {lastPermit && (
        <Card style={{ backgroundColor: "#fff7ed", borderColor: "#fed7aa" }}>
          <CardHeader>
            <CardTitle style={{ fontSize: "1rem", color: "#9a3412" }}>Permiso generado</CardTitle>
            <CardDescription>El permiso ha sido emitido correctamente.</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: "0.75rem", color: "#9a3412", textTransform: "uppercase", letterSpacing: 0.5 }}>Folio</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#9a3412" }}>{lastPermit.folio_short}</div>
            </div>
            <div style={{ fontSize: "0.85rem", color: "#7c2d12", marginBottom: 4 }}><b>Vehículo:</b> {lastPermit.marca} {lastPermit.linea} {lastPermit.modelo}</div>
            <div style={{ fontSize: "0.85rem", color: "#7c2d12", marginBottom: 4 }}><b>Propietario:</b> {lastPermit.owner_name}</div>
            <div style={{ fontSize: "0.85rem", color: "#7c2d12", marginBottom: 12 }}><b>Vigencia:</b> 30 días naturales</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                type="button"
                onClick={() => openPrintView(lastPermit.folio_short)}
                variant="outline"
                style={{ flex: 1 }}
              >
                <Printer size={14} style={{ marginRight: 6 }} /> Ver / Imprimir
              </Button>
              <Button
                type="button"
                onClick={() => handleDownloadPdf(lastPermit.folio_short)}
                style={{ backgroundColor: "#f58238", color: "white", flex: 1 }}
              >
                <Download size={14} style={{ marginRight: 6 }} /> Descargar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label style={{ marginBottom: 6, display: "block", fontSize: "0.85rem", color: "#374151" }}>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
